import uuid from 'uuid/v4';
import Auth from '@Services/Auth';
import { RxSocketUrls, socketConfig } from '@Constants/CommonConstants';
import SimpleTmpl from '@Services/SimpleTmpl';
import RequestUrls from '@Constants/RequestUrlsConstants';
import SocketBroker from '@Services/SocketBroker';

export const HEATMAP_THUMBNAIL_CONFIG = {
  ATTEMPT_TIMEOUT: 30000,
  MAX_ATTEMPTS: 2,
  THUMBNAIL_ABORTED: 'ABORTED',
  THUMBNAIL_LIMIT: 'TIMEOUT_LIMIT',
};

class HeatmapNavigatorImageService {
  _socketSubject;

  _timeoutId;

  _defer;

  _onThumbnailNotify = ({ payload = {} } = {}) => {
    Logger.info('Received response (_onThumbnailNotify)', payload);
    switch (payload.taskStatus) {
      // IMAGE_GENERATING status is sent when image generating task has been submitted to image generating workers.
      // On receiving IMAGE_GENERATING OR COMPLETED statuses - reuse regular image load procedure
      case 'IMAGE_GENERATING':
      case 'COMPLETED':
        this._onThumbnailReceived(payload.path);
        this._cancel(socketConfig.REASON_CLEANUP);
        break;
      case 'ABORTED':
        this._onThumbnailError(HEATMAP_THUMBNAIL_CONFIG.THUMBNAIL_ABORTED);
        this._cancel(socketConfig.REASON_CLEANUP);
        break;
      default:
        break;
    }
  };

  _onThumbnailReceived = (imgPath) => {
    const accessToken = Auth.getAccessToken();
    this._defer.resolve(
      SimpleTmpl.format(
        RequestUrls.relativeResultsImage.url,
        {
          imgPath,
          accessToken,
        },
        true
      )
    );
  };

  _onThumbnailError = (reason) => {
    this._defer.reject(reason);
  };

  _cancel = (reason, onlySocket = false) => {
    if (!onlySocket && this._timeoutId) clearTimeout(this._timeoutId);
    if (this._socketSubject) this._socketSubject.cancel(reason);
  };

  _fetchThumbnail = (matrixName, attempt = 1) => {
    // Check number of attempts
    if (attempt > HEATMAP_THUMBNAIL_CONFIG.MAX_ATTEMPTS) {
      this._onThumbnailError(HEATMAP_THUMBNAIL_CONFIG.THUMBNAIL_LIMIT);
      return;
    }
    this._timeoutId = setTimeout(() => {
      this._cancel(socketConfig.REASON_CANCEL, true);
      this._fetchThumbnail(matrixName, attempt + 1);
    }, HEATMAP_THUMBNAIL_CONFIG.ATTEMPT_TIMEOUT);

    // Main retrieving logic
    const requestId = uuid();
    const payload = {
      requestId,
      matrixName,
    };
    this._socketSubject = SocketBroker.send(
      {
        send: RxSocketUrls.HeatmapThumbnail.input,
        message: RxSocketUrls.HeatmapThumbnail.output,
      },
      {
        params: payload,
        filter: ({ info = {} }) => info.requestId === requestId,
      }
    );

    this._socketSubject.subscribe({
      next: ({ type, data }) => (type === 'progress' ? this._onThumbnailNotify(data) : null),
      error: Logger.error,
    });
  };

  getThumbnail = (matrixName) => {
    this._socketSubject = null;
    this._timeoutId = null;
    this._defer = {};

    // create promise
    const promise = new Promise((resolve, reject) => {
      Object.assign(this._defer, {
        resolve,
        reject,
      });
    });

    // add cancel method
    Object.assign(promise, {
      cancel: () => {
        this._cancel(socketConfig.REASON_CANCEL);
      },
    });

    // first attempt
    this._fetchThumbnail(matrixName, 1);

    return promise;
  };
}

export default new HeatmapNavigatorImageService();
